import { lazy, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useOutletContext } from 'react-router-dom';

import FilterDefault from 'app/components/Filter/FilterDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { DEFAULT_TABLE_QUERY, QUERIES } from 'app/components/Table/core/constants';
import { ID } from 'app/components/Table/core/types';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import { TippyDefault } from 'app/components/Tippy';

import { useAuth } from 'app/modules/auth';
import { SensorSkuId } from 'app/modules/inventory/bins/core/enum';
import {
  checkParamSynced,
  useGetQueryParams,
  useSyncQueryToState,
} from 'app/modules/query-state/hooks';
import { TimeframeExport } from 'app/modules/reporting/components/TimeframeExport';
import DeviceSensorFilter from 'app/modules/system-settings/devices/components/DeviceSensorFilter';
import ExportButton from 'app/modules/system-settings/devices/components/ExportButton';
import { SensorType } from 'app/modules/system-settings/devices/core/enum';
import { DeviceSensorFilterParams, Sensor } from 'app/modules/system-settings/devices/core/type';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import { usePolicyInUser } from 'app/hooks/policy';
import { SocketClient } from 'app/hooks/socket';
import { isNil } from 'lodash';

import { sensorsColumns } from './core/columns';
import { deleteSensor, downloadExpirySensor, downloadHeartbeat, getSensors } from './core/requests';
import { FormDeviceSensorCard } from './modal/FormDeviceSensorCard';
import { SuspensedView } from 'app/routing/PrivateRoutes';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const UpdateBaseWeightModal = lazy(
  () => import('app/modules/inventory/racks/modal/UpdateBaseWeightModal'),
);

const DeviceSensorManagementPage = () => {
  useSyncQueryToState(window.location.pathname, DEFAULT_TABLE_QUERY);

  const wards = useOutletContext();

  const [sensor, setSensor] = useState<Sensor>();
  const [openTimeframeExport, setOpenTimeframeExport] = useState(false);
  const [isShowUpdateBaseWeightModal, setIsShowUpdateBaseWeightModal] = useState(false);
  const { currentUser } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdminCurrent = permission.systemAdmin;
  const organization = useOrganizationStore((state) => state.state.organization);

  const [isShowFilter, setIsShowFilter] = useState(false);

  const queryClient = useQueryClient();

  const { synced, curParams } = useGetQueryParams<DeviceSensorFilterParams>();
  const isSynced = checkParamSynced(synced);

  const selectedEntity = {
    baseWeight: sensor?.baseWeight ?? 0,
    id: sensor?.id,
    code: sensor?.code ?? '',
  };

  useEffect(() => {
    if (isSynced) {
      queryClient.invalidateQueries(QUERIES.DEVICE_SENSOR_LIST);
    }
  }, [isSynced, JSON.stringify(curParams)]);

  useEffect(() => {
    SocketClient.onHeartbeatStatusChanged(() => {
      queryClient.invalidateQueries(QUERIES.DEVICE_SENSOR_LIST);
    });
    return () => {
      SocketClient.offHeartbeatStatusChanged();
    };
  }, [queryClient]);

  const hasActiveFilters = Object.keys(curParams).some(
    (key) => ['status', 'locationIds'].includes(key) && !isNil(curParams[key]),
  );

  const handleClickFilter = (type: 'on' | 'off') => {
    setIsShowFilter(type === 'on');
  };

  const renderModalForm = (originalData: Sensor) => {
    return (
      <FormDeviceSensorCard
        sensor={originalData}
        titleNotification="Sensor"
        wards={wards as Ward[]}
      />
    );
  };

  const handleGetDataItem = (item: Sensor) => setSensor(item);

  const { mutate: mutateDownloadExpirySensor, isLoading: isDownloadingExpirySensor } = useMutation({
    mutationKey: ['download-expiry-sensor'],
    mutationFn: (params: { binId: ID }) => downloadExpirySensor(params),
  });

  const { mutate: mutateDownloadHeartbeat, isLoading: isLoadingHeartbeat } = useMutation({
    mutationKey: ['download-heartbeat'],
    mutationFn: (params: { from: string; to: string }) => downloadHeartbeat(params),
    onSuccess: () => {
      setOpenTimeframeExport(false);
    },
  });

  const handleDownloadExpirySensor = (binId: ID) => () => mutateDownloadExpirySensor({ binId });

  const isDownloading = isDownloadingExpirySensor;

  const handleDeleteSensor = async (id: ID) => {
    let type = SensorType.SENSOR_CV_EXPIRY;
    if (sensor?.skuId === SensorSkuId.CV) type = SensorType.SENSOR_CV;
    if (sensor?.skuId === SensorSkuId.Weight) type = SensorType.SENSOR_WEIGHT;
    if (sensor?.skuId === SensorSkuId.Hanger) type = SensorType.SENSOR_HANGER;
    await deleteSensor(id, type);
  };

  const handleCloseTimeframeExport = () => setOpenTimeframeExport(false);

  const handleClickExportHeartbeat = () => setOpenTimeframeExport(true);

  const handleExportHeartbeat = (filter: { fromDate: Date; toDate: Date }) => {
    const { fromDate, toDate } = filter;

    const from = new Date(
      Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate()),
    );
    const to = new Date(
      Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59, 999),
    );

    mutateDownloadHeartbeat({
      from: from.getTime().toString(),
      to: to.getTime().toString(),
    });
  };

  const handleShowUpdateBaseWeightModal = (entity: Sensor) => () => {
    setSensor(entity);
    setIsShowUpdateBaseWeightModal(true);
  };

  const handleCloseUpdateBaseWeightModal = () => {
    setIsShowUpdateBaseWeightModal(false);
    setSensor(undefined);
  };

  const handleRefreshList = () => {
    queryClient.invalidateQueries(QUERIES.DEVICE_SENSOR_LIST);
  };

  const columns = useMemo(
    () =>
      sensorsColumns(
        handleDownloadExpirySensor,
        handleShowUpdateBaseWeightModal,
        isSystemAdminCurrent,
      ),
    [handleDownloadExpirySensor, handleShowUpdateBaseWeightModal, isSystemAdminCurrent],
  );

  return (
    <>
      <TableComponent
        getList={getSensors}
        nameQuery={QUERIES.DEVICE_SENSOR_LIST}
        moreQuery={isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''}
        originColumns={columns}
        titleModal="sensor"
        ModalForm={renderModalForm}
        nameBtnAdd="Add sensor"
        deleteById={handleDeleteSensor as any}
        syncParamUrl
        placeholderSearch="Search with sensor"
        getDataItem={handleGetDataItem}
        listHeaderPosition="left"
        customListHeader={
          <>
            <FilterDefault
              visible={isShowFilter}
              extraClassBtnFilter={isSystemAdminCurrent ? 'm-0' : ''}
              onClickFilter={handleClickFilter}
              isFilter={hasActiveFilters}
              content={
                isShowFilter && (
                  <DeviceSensorFilter
                    openFilter={isShowFilter}
                    setIsOpenFilter={setIsShowFilter}
                    locations={wards as Ward[]}
                  />
                )
              }
            />

            {isSystemAdminCurrent && (
              <>
                <ExportButton
                  text="Export Heartbeat"
                  onButtonClick={handleClickExportHeartbeat}
                  className="mx-8"
                />

                <div className="position-absolute end-0" style={{ top: '6rem', zIndex: 100 }}>
                  <TippyDefault
                    containerClass="action-menu"
                    placement="bottom-start"
                    offset={[0, 10]}
                    interactive
                    arrow={false}
                    trigger="click"
                    visible={openTimeframeExport}
                    onClickOutside={handleCloseTimeframeExport}
                    allowHTML
                    content={
                      openTimeframeExport && (
                        <TimeframeExport
                          onExport={handleExportHeartbeat}
                          onClose={handleCloseTimeframeExport}
                          isSubmitting={isLoadingHeartbeat}
                          title="Export Heartbeat"
                          maxMonths={3}
                        />
                      )
                    }
                  >
                    <span className="hidden"></span>
                  </TippyDefault>
                </div>
              </>
            )}
          </>
        }
      />

      <SuspensedView>
        {isShowUpdateBaseWeightModal && (
          <UpdateBaseWeightModal
            selectedEntity={selectedEntity}
            onClickCloseModal={handleCloseUpdateBaseWeightModal}
            isRefreshList={true}
            callback={handleRefreshList}
          />
        )}
      </SuspensedView>

      {isDownloading && <LoadingCard />}
    </>
  );
};

export default DeviceSensorManagementPage;
